import type { Field, OptionGroup } from 'react-querybuilder'
import { toFullOption } from 'react-querybuilder'

const TEXT_OPERATORS = [
  { name: '=', label: 'is' },
  { name: '!=', label: 'is not' },
]

const DATE_OPERATORS = [
  { name: '=', label: 'is' },
  { name: '!=', label: 'is not' },
  { name: '<', label: 'is before' },
  { name: '>', label: 'is after' },
  { name: '<=', label: 'is on or before' },
  { name: '>=', label: 'is on or after' },
]

const NUMBER_OPERATORS = [
  { name: '=', label: 'equal' },
  { name: '!=', label: 'not equal' },
  { name: '<', label: 'less than' },
  { name: '>', label: 'greater than' },
  { name: '<=', label: 'less than or equal' },
  { name: '>=', label: 'greater than or equal' },
]

const buildOptions = (label: string, values: string[]): OptionGroup[] => [
  { label, options: values.map((s) => ({ name: s, label: s })) },
]

const genderValues = buildOptions('Gender', ['Male', 'Female'])

const relationshipValues = buildOptions('Relationship', [
  'Employee',
  'Spouse',
  'Domestic Partner',
  'Child',
  'Child of Domestic Partner',
])

const marriageStatusValues = buildOptions('Status', [
  'Single',
  'Married',
  'Domestic Partner',
  'Divorced',
  'Widowed',
  'Other',
])

const smokerValues = buildOptions('Smoker', ['TRUE', 'FALSE'])

const isExemptValues = buildOptions('IsExempt', ['TRUE', 'FALSE'])

const employmentStatusValues = buildOptions('Status', [
  'Full-Time',
  'Part-Time',
  'Terminated',
])

const employmentTypeValues = buildOptions('Type', [
  'Full-Time',
  'Part-Time',
  'Contractor',
  'Temporary',
  'Seasonal',
])

const emailAddressTypeValues = buildOptions('EmailAddressType', [
  'Home',
  'Work',
])

const phoneTypeValues = buildOptions('PhoneType', [
  'Mobile',
  'Home',
  'Work',
  'Unknown',
])

const payrollCycleValues = buildOptions('PayrollCycle', [
  '12',
  '24',
  '26',
  '52',
])

const textFields: Field[] = [
  {
    name: 'CompanyEmployeeId',
    label: 'Company Employee ID',
    operators: TEXT_OPERATORS,
  },
  { name: 'FirstName', label: 'First Name', operators: TEXT_OPERATORS },
  { name: 'LastName', label: 'Last Name', operators: TEXT_OPERATORS },
  { name: 'SSN', label: 'SSN', operators: TEXT_OPERATORS },
  { name: 'EmailAddress', label: 'Email Address', operators: TEXT_OPERATORS },
  { name: 'PhoneNumber', label: 'Phone Number', operators: TEXT_OPERATORS },
  { name: 'StreetAddress', label: 'Street Address', operators: TEXT_OPERATORS },
  {
    name: 'StreetAddressExt',
    label: 'Street Address Ext',
    operators: TEXT_OPERATORS,
  },
  { name: 'City', label: 'City', operators: TEXT_OPERATORS },
  {
    name: 'StateProvince',
    label: 'State / Province',
    operators: TEXT_OPERATORS,
  },
  { name: 'PostalCode', label: 'Postal Code', operators: TEXT_OPERATORS },
  { name: 'County', label: 'County', operators: TEXT_OPERATORS },
  {
    name: 'MailingStreetAddress',
    label: 'Mailing Street Address',
    operators: TEXT_OPERATORS,
  },
  {
    name: 'MailingStreetAddressExt',
    label: 'Mailing Street Address Ext',
    operators: TEXT_OPERATORS,
  },
  { name: 'MailingCity', label: 'Mailing City', operators: TEXT_OPERATORS },
  {
    name: 'MailingPostalCode',
    label: 'Mailing Postal Code',
    operators: TEXT_OPERATORS,
  },
  {
    name: 'MailingStateProvince',
    label: 'Mailing State / Province',
    operators: TEXT_OPERATORS,
  },
  { name: 'JobTitle', label: 'Job Title', operators: TEXT_OPERATORS },
  { name: 'CompanyCode', label: 'Company Code', operators: TEXT_OPERATORS },
  { name: 'DivisionCode', label: 'Division Code', operators: TEXT_OPERATORS },
  {
    name: 'DepartmentCode',
    label: 'Department Code',
    operators: TEXT_OPERATORS,
  },
  { name: 'Department', label: 'Department', operators: TEXT_OPERATORS },
  { name: 'Eligibility_1', label: 'Eligibility 1', operators: TEXT_OPERATORS },
  { name: 'Eligibility_2', label: 'Eligibility 2', operators: TEXT_OPERATORS },
  { name: 'Eligibility_3', label: 'Eligibility 3', operators: TEXT_OPERATORS },
  { name: 'Eligibility_4', label: 'Eligibility 4', operators: TEXT_OPERATORS },
  {
    name: 'ContributionGroup',
    label: 'Contribution Group',
    operators: TEXT_OPERATORS,
  },
]

const selectFields: Field[] = [
  {
    name: 'Gender',
    label: 'Gender',
    valueEditorType: 'select',
    values: genderValues,
    operators: TEXT_OPERATORS,
  },
  {
    name: 'Relationship',
    label: 'Relationship',
    valueEditorType: 'select',
    values: relationshipValues,
    operators: TEXT_OPERATORS,
  },
  {
    name: 'MarriageStatus',
    label: 'Marriage Status',
    valueEditorType: 'select',
    values: marriageStatusValues,
    operators: TEXT_OPERATORS,
  },
  {
    name: 'Smoker',
    label: 'Smoker',
    valueEditorType: 'select',
    values: smokerValues,
    operators: TEXT_OPERATORS,
  },
  {
    name: 'EmailAddressType',
    label: 'Email Address Type',
    valueEditorType: 'select',
    values: emailAddressTypeValues,
    operators: TEXT_OPERATORS,
  },
  {
    name: 'PhoneType',
    label: 'Phone Type',
    valueEditorType: 'select',
    values: phoneTypeValues,
    operators: TEXT_OPERATORS,
  },
  {
    name: 'EmploymentType',
    label: 'Employment Type',
    valueEditorType: 'select',
    values: employmentTypeValues,
    operators: TEXT_OPERATORS,
  },
  {
    name: 'EmploymentStatus',
    label: 'Employment Status',
    valueEditorType: 'select',
    values: employmentStatusValues,
    operators: TEXT_OPERATORS,
  },
  {
    name: 'IsExempt',
    label: 'Is Exempt',
    valueEditorType: 'select',
    values: isExemptValues,
    operators: TEXT_OPERATORS,
  },
  {
    name: 'PayrollCycle',
    label: 'Payroll Cycle',
    valueEditorType: 'select',
    values: payrollCycleValues,
    operators: TEXT_OPERATORS,
  },
]

export const dateFields: Field[] = [
  {
    name: 'DateOfBirth',
    label: 'Date Of Birth',
    operators: DATE_OPERATORS,
    inputType: 'date',
  },
  {
    name: 'HireDate',
    label: 'Hire Date',
    operators: DATE_OPERATORS,
    inputType: 'date',
    datatype: 'date',
  },
  {
    name: 'ReHireDate',
    label: 'Re-Hire Date',
    operators: DATE_OPERATORS,
    inputType: 'date',
    datatype: 'date',
  },
  {
    name: 'EnrollmentStartDate',
    label: 'Enrollment Start Date',
    operators: DATE_OPERATORS,
    inputType: 'date',
  },
  {
    name: 'EnrollmentEndDate',
    label: 'Enrollment End Date',
    operators: DATE_OPERATORS,
    inputType: 'date',
  },
]

export const numberFields: Field[] = [
  {
    name: 'Salary',
    label: 'Salary',
    operators: NUMBER_OPERATORS,
  },
  {
    name: 'HourlyWage',
    label: 'Hourly Wage',
    operators: NUMBER_OPERATORS,
  },
]

export const fields = (
  [
    ...textFields,
    ...selectFields,
    ...dateFields,
    ...numberFields,
  ] satisfies Field[]
).map((o) => toFullOption(o))
